/**
 * Cola de mutaciones CRUD para modo offline
 *
 * Almacena POST/PUT/DELETE en IndexedDB cuando offline.
 * Al reconectar, replay FIFO usando fetch nativo con auth headers.
 *
 * @module services/mutationQueue
 */

import { createBaseQueue } from './baseQueue';
import { createAuthStorage } from './base/authStorage';
import offlineDb from './offlineDb';

const authStorage = createAuthStorage();

const mutationQueue = createBaseQueue({
  maxRetries: 3,
  backoffBaseMs: 1000,

  async processItem(item) {
    let headers;
    try {
      headers = authStorage.getAuthHeaders();
    } catch (err) {
      // Token expirado o ausente
      if (err.message && (err.message.includes('token') || err.message.includes('expired'))) {
        item.status = 'auth_error';
        throw err;
      }
      throw err;
    }

    const fetchOptions = {
      method: item.method,
      headers
    };
    if (item.body) {
      fetchOptions.body = item.body;
    }

    const response = await fetch(item.endpoint, fetchOptions);

    if (response.status === 409) {
      item.status = 'conflict';
      throw new Error('Conflicto: el recurso fue modificado por otro usuario');
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
  },

  async persist(queue) {
    try {
      const db = await offlineDb.getDb();
      const tx = db.transaction('mutationQueue', 'readwrite');
      await tx.store.clear();
      for (const item of queue) {
        await tx.store.put({
          method: item.method,
          endpoint: item.endpoint,
          body: item.body,
          timestamp: item.timestamp || item.addedAt,
          status: item.status,
          retries: item.retries
        });
      }
      await tx.done;
    } catch { /* ignore */ }
  },

  async restore() {
    try {
      const items = await offlineDb.getPendingMutations();
      return items.map(item => ({
        ...item,
        status: item.status === 'processing' ? 'pending' : (item.status || 'pending')
      }));
    } catch {
      return [];
    }
  }
});

export default mutationQueue;
